'use client';

import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useAuth } from '@/lib/auth';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';
import Link from 'next/link';

interface SLATier {
  name: string;
  uptime: number;
  responseTime: string;
  supportHours: string;
  escalation: string;
  price?: string;
}

interface ServiceType {
  id: string;
  name: string;
  description: string;
  defaultTiers: SLATier[];
}

const SERVICE_TYPES: ServiceType[] = [
  {
    id: 'api',
    name: 'API Service',
    description: 'REST/GraphQL API endpoints',
    defaultTiers: [
      { name: 'Bronze', uptime: 99.0, responseTime: '< 500ms', supportHours: 'Business hours', escalation: '4 hours' },
      { name: 'Silver', uptime: 99.9, responseTime: '< 200ms', supportHours: '24/5', escalation: '1 hour' },
      { name: 'Gold', uptime: 99.99, responseTime: '< 100ms', supportHours: '24/7', escalation: '15 minutes' },
    ],
  },
  {
    id: 'platform',
    name: 'Platform/SaaS',
    description: 'Web application platform',
    defaultTiers: [
      { name: 'Bronze', uptime: 99.5, responseTime: '< 2s page load', supportHours: 'Business hours', escalation: '8 hours' },
      { name: 'Silver', uptime: 99.9, responseTime: '< 1s page load', supportHours: '24/5', escalation: '2 hours' },
      { name: 'Gold', uptime: 99.95, responseTime: '< 500ms page load', supportHours: '24/7', escalation: '30 minutes' },
    ],
  },
  {
    id: 'infrastructure',
    name: 'Infrastructure/Hosting',
    description: 'Server hosting and infrastructure',
    defaultTiers: [
      { name: 'Bronze', uptime: 99.0, responseTime: 'N/A', supportHours: 'Business hours', escalation: '12 hours' },
      { name: 'Silver', uptime: 99.9, responseTime: 'N/A', supportHours: '24/7', escalation: '4 hours' },
      { name: 'Gold', uptime: 99.99, responseTime: 'N/A', supportHours: '24/7', escalation: '1 hour' },
    ],
  },
  {
    id: 'database',
    name: 'Database Service',
    description: 'Managed database hosting',
    defaultTiers: [
      { name: 'Bronze', uptime: 99.5, responseTime: '< 100ms query', supportHours: 'Business hours', escalation: '4 hours' },
      { name: 'Silver', uptime: 99.9, responseTime: '< 50ms query', supportHours: '24/7', escalation: '1 hour' },
      { name: 'Gold', uptime: 99.99, responseTime: '< 10ms query', supportHours: '24/7', escalation: '15 minutes' },
    ],
  },
  {
    id: 'cdn',
    name: 'CDN/Edge',
    description: 'Content delivery network',
    defaultTiers: [
      { name: 'Bronze', uptime: 99.5, responseTime: '< 200ms', supportHours: 'Business hours', escalation: '8 hours' },
      { name: 'Silver', uptime: 99.9, responseTime: '< 100ms', supportHours: '24/7', escalation: '2 hours' },
      { name: 'Gold', uptime: 99.99, responseTime: '< 50ms', supportHours: '24/7', escalation: '30 minutes' },
    ],
  },
];

export default function SLAGeneratorPage() {
  const [serviceType, setServiceType] = useState<ServiceType>(SERVICE_TYPES[0]);
  const [customServiceName, setCustomServiceName] = useState('');
  const [tiers, setTiers] = useState<SLATier[]>(SERVICE_TYPES[0].defaultTiers);
  const [selectedTier, setSelectedTier] = useState<SLATier>(SERVICE_TYPES[0].defaultTiers[1]);
  const [annualRevenue, setAnnualRevenue] = useState<number>(1000000);
  const [penaltyPercentage, setPenaltyPercentage] = useState<number>(10);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'sla-generator',
    toolCategory: 'generator',
  });

  // Premium access control
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<{ plan: 'free' | 'pro' | 'team'; status: string } | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);

  const TIER_HIERARCHY = {
    free: 0,
    pro: 1,
    team: 2,
  };

  // Fetch user subscription status
  useEffect(() => {
    async function fetchSubscription() {
      if (!isAuthenticated || !user) {
        setLoadingSubscription(false);
        return;
      }

      try {
        const response = await fetch('/api/secure/dashboard', {
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setSubscription(data.data.subscription);
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
      } finally {
        setLoadingSubscription(false);
      }
    }

    fetchSubscription();
  }, [isAuthenticated, user]);

  const userTier = subscription?.plan || 'free';
  const hasProAccess = TIER_HIERARCHY[userTier] >= TIER_HIERARCHY.pro;

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Update tiers when service type changes
  const handleServiceTypeChange = (newServiceType: ServiceType) => {
    setServiceType(newServiceType);
    setTiers(newServiceType.defaultTiers);
    setSelectedTier(newServiceType.defaultTiers[1]); // Default to Silver
  };

  // Calculate downtime allowance
  const calculateDowntime = (uptime: number) => {
    const downtimePercentage = 100 - uptime;
    const monthlyMinutes = 30 * 24 * 60; // ~43,200 minutes per month
    const yearlyMinutes = 365 * 24 * 60; // ~525,600 minutes per year

    const monthlyDowntime = monthlyMinutes * (downtimePercentage / 100);
    const yearlyDowntime = yearlyMinutes * (downtimePercentage / 100);

    return {
      monthlyMinutes: monthlyDowntime.toFixed(2),
      monthlyHours: (monthlyDowntime / 60).toFixed(2),
      yearlyHours: (yearlyDowntime / 60).toFixed(2),
      yearlyDays: (yearlyDowntime / 60 / 24).toFixed(2),
    };
  };

  // Calculate financial penalties
  const calculatePenalty = (uptime: number) => {
    const penalty = annualRevenue * (penaltyPercentage / 100);
    const monthlyPenalty = penalty / 12;
    return {
      total: penalty.toFixed(2),
      monthly: monthlyPenalty.toFixed(2),
    };
  };

  // Export to Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    if (!wb.Workbook) wb.Workbook = {};
    (wb.Workbook as any).CalcPr = { fullCalcOnLoad: true };

    // SLA Overview Sheet
    const overviewData: any[] = [
      ['SLA AGREEMENT - OVERVIEW'],
      [''],
      ['Service Information'],
      ['Service Type:', serviceType.name],
      ['Service Name:', customServiceName || serviceType.name],
      ['Description:', serviceType.description],
      [''],
      ['SLA Tier Comparison'],
      [''],
      ['Tier', 'Uptime %', 'Monthly Downtime', 'Yearly Downtime', 'Response Time', 'Support', 'Escalation'],
    ];

    tiers.forEach(tier => {
      const downtime = calculateDowntime(tier.uptime);
      overviewData.push([
        tier.name,
        tier.uptime + '%',
        `${downtime.monthlyMinutes} min (${downtime.monthlyHours} hrs)`,
        `${downtime.yearlyHours} hrs (${downtime.yearlyDays} days)`,
        tier.responseTime,
        tier.supportHours,
        tier.escalation,
      ]);
    });

    const wsOverview = XLSX.utils.aoa_to_sheet(overviewData);
    wsOverview['!cols'] = [
      { wch: 20 }, // Tier
      { wch: 12 }, // Uptime
      { wch: 25 }, // Monthly Downtime
      { wch: 25 }, // Yearly Downtime
      { wch: 18 }, // Response Time
      { wch: 18 }, // Support
      { wch: 15 }, // Escalation
    ];
    XLSX.utils.book_append_sheet(wb, wsOverview, 'SLA Overview');

    // Selected Tier Details
    const tierData: any[] = [
      ['SLA AGREEMENT - TIER DETAILS'],
      [''],
      ['Selected Tier:', selectedTier.name],
      ['Target Uptime:', selectedTier.uptime + '%'],
      [''],
      ['AVAILABILITY CALCULATIONS'],
      [''],
      ['Period', 'Total Time', 'Allowed Downtime'],
    ];

    const downtime = calculateDowntime(selectedTier.uptime);
    tierData.push(
      ['Monthly', '720 hours', `${downtime.monthlyHours} hours (${downtime.monthlyMinutes} minutes)`],
      ['Yearly', '8,760 hours', `${downtime.yearlyHours} hours (${downtime.yearlyDays} days)`]
    );

    tierData.push(
      [''],
      ['PERFORMANCE COMMITMENTS'],
      [''],
      ['Metric', 'Commitment'],
      ['Response Time', selectedTier.responseTime],
      ['Support Hours', selectedTier.supportHours],
      ['Escalation Time', selectedTier.escalation],
      [''],
      ['FINANCIAL TERMS'],
      [''],
      ['Annual Revenue:', `$${annualRevenue.toLocaleString()}`],
      ['Penalty Percentage:', `${penaltyPercentage}%`],
    );

    const penalty = calculatePenalty(selectedTier.uptime);
    tierData.push(
      ['Annual Penalty (if SLA breached):', `$${penalty.total}`],
      ['Monthly Penalty:', `$${penalty.monthly}`]
    );

    const wsTier = XLSX.utils.aoa_to_sheet(tierData);
    wsTier['!cols'] = [{ wch: 35 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, wsTier, 'Selected Tier');

    // SLA Document Template
    const documentData: any[] = [
      ['SERVICE LEVEL AGREEMENT'],
      [''],
      ['1. PARTIES'],
      ['This Service Level Agreement ("SLA") is between [Provider Name] and [Customer Name].'],
      [''],
      ['2. SERVICE DESCRIPTION'],
      [`Service Type: ${serviceType.name}`],
      [`Service Name: ${customServiceName || serviceType.name}`],
      [`Description: ${serviceType.description}`],
      [''],
      ['3. SERVICE LEVEL COMMITMENTS'],
      [''],
      ['3.1 Availability'],
      [`Provider commits to ${selectedTier.uptime}% uptime for the ${selectedTier.name} tier.`],
      [`Allowed downtime: ${downtime.monthlyHours} hours per month, ${downtime.yearlyHours} hours per year.`],
      [''],
      ['3.2 Performance'],
      [`Response Time: ${selectedTier.responseTime}`],
      [`Support Hours: ${selectedTier.supportHours}`],
      [`Escalation Time: ${selectedTier.escalation}`],
      [''],
      ['3.3 Measurement'],
      ['Uptime percentage is calculated as: (Total Minutes - Downtime Minutes) / Total Minutes × 100'],
      ['Measurements are taken on a monthly basis.'],
      ['Scheduled maintenance windows (announced 7 days in advance) are excluded.'],
      [''],
      ['4. SERVICE CREDITS / PENALTIES'],
      [''],
      [`If uptime falls below ${selectedTier.uptime}%, Customer is entitled to:`],
      [`- ${penaltyPercentage}% of monthly fees (${selectedTier.uptime}% - ${selectedTier.uptime - 0.5}% uptime)`],
      [`- ${penaltyPercentage * 2}% of monthly fees (${selectedTier.uptime - 0.5}% - ${selectedTier.uptime - 1}% uptime)`],
      [`- ${penaltyPercentage * 3}% of monthly fees (below ${selectedTier.uptime - 1}% uptime)`],
      [''],
      ['Maximum monthly credit: 100% of monthly fees'],
      [''],
      ['5. EXCLUSIONS'],
      ['The following are excluded from SLA calculations:'],
      ['- Scheduled maintenance (with 7 days notice)'],
      ['- Emergency maintenance (security patches)'],
      ['- Issues caused by Customer misuse or third-party services'],
      ['- Force majeure events'],
      ['- DDoS attacks or other malicious activities'],
      [''],
      ['6. SUPPORT'],
      [''],
      [`Support Hours: ${selectedTier.supportHours}`],
      [`Initial Response Time: ${selectedTier.escalation}`],
      ['Support Channels: Email, Phone, Chat (as applicable)'],
      [''],
      ['7. MONITORING & REPORTING'],
      ['Provider will monitor service availability 24/7'],
      ['Monthly SLA reports will be provided within 5 business days of month end'],
      ['Real-time status page available at [URL]'],
      [''],
      ['8. CLAIM PROCESS'],
      ['Customer must submit SLA credit claims within 30 days of incident'],
      ['Provider will review and respond within 15 business days'],
      ['Credits will be applied to next monthly invoice'],
      [''],
      ['9. TERM & TERMINATION'],
      ['This SLA is effective upon service activation'],
      ['Either party may request SLA review quarterly'],
      ['SLA terminates upon service termination'],
      [''],
      ['10. AMENDMENTS'],
      ['Provider may update SLA with 60 days written notice'],
      ['Material changes require Customer consent'],
      [''],
      ['SIGNATURES'],
      [''],
      ['Provider: _________________________ Date: _________'],
      ['Customer: _________________________ Date: _________'],
      [''],
      ['Generated:', new Date().toLocaleString()],
    ];

    const wsDocument = XLSX.utils.aoa_to_sheet(documentData);
    wsDocument['!cols'] = [{ wch: 100 }];
    XLSX.utils.book_append_sheet(wb, wsDocument, 'SLA Document');

    // Write file
    const filename = `SLA_${serviceType.name.replace(/\s+/g, '_')}_${selectedTier.name}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    showToast('✓ SLA exported to Excel successfully!', 'success');
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const downtime = calculateDowntime(selectedTier.uptime);
    const penalty = calculatePenalty(selectedTier.uptime);

    // Generate the SLA document content as text
    const slaContent = [
      `SERVICE LEVEL AGREEMENT`,
      ``,
      `Service Type: ${serviceType.name}`,
      `Service Name: ${customServiceName || serviceType.name}`,
      `Description: ${serviceType.description}`,
      ``,
      `SLA TIER: ${selectedTier.name}`,
      ``,
      `AVAILABILITY COMMITMENTS:`,
      `- Target Uptime: ${selectedTier.uptime}%`,
      `- Allowed Monthly Downtime: ${downtime.monthlyHours} hours (${downtime.monthlyMinutes} minutes)`,
      `- Allowed Yearly Downtime: ${downtime.yearlyHours} hours (${downtime.yearlyDays} days)`,
      ``,
      `PERFORMANCE COMMITMENTS:`,
      `- Response Time: ${selectedTier.responseTime}`,
      `- Support Hours: ${selectedTier.supportHours}`,
      `- Escalation Time: ${selectedTier.escalation}`,
      ``,
      `FINANCIAL TERMS:`,
      `- Annual Revenue: $${annualRevenue.toLocaleString()}`,
      `- Penalty Percentage: ${penaltyPercentage}%`,
      `- Annual Penalty (if breached): $${penalty.total}`,
      `- Monthly Penalty: $${penalty.monthly}`,
      ``,
      `Generated: ${new Date().toLocaleString()}`,
    ].join('\n');

    const assessment = await saveResult({
      name: `SLA: ${customServiceName || serviceType.name} - ${selectedTier.name} Tier`,
      metadata: {
        serviceType: serviceType.name,
        serviceName: customServiceName || serviceType.name,
        serviceDescription: serviceType.description,
        selectedTier: selectedTier.name,
        uptime: selectedTier.uptime,
        responseTime: selectedTier.responseTime,
        supportHours: selectedTier.supportHours,
        escalation: selectedTier.escalation,
        monthlyDowntimeMinutes: downtime.monthlyMinutes,
        monthlyDowntimeHours: downtime.monthlyHours,
        yearlyDowntimeHours: downtime.yearlyHours,
        yearlyDowntimeDays: downtime.yearlyDays,
        annualRevenue,
        penaltyPercentage,
        annualPenalty: penalty.total,
        monthlyPenalty: penalty.monthly,
        allTiers: tiers.map(t => ({
          name: t.name,
          uptime: t.uptime,
          responseTime: t.responseTime,
          supportHours: t.supportHours,
          escalation: t.escalation,
        })),
        slaDocument: slaContent,
      },
    });

    if (assessment) {
      setSaved(true);
      showToast('SLA saved to Command Center', 'success');
    }
  };

  return (
    <div className="min-h-screen bg-background py-12">
      {/* Toast Notification */}
      {toast && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg border-2 transform transition-all duration-300 ${
            toast.type === 'success'
              ? 'bg-green-900/90 border-green-500 text-green-100'
              : toast.type === 'error'
              ? 'bg-red-900/90 border-red-500 text-red-100'
              : 'bg-blue-900/90 border-blue-500 text-blue-100'
          }`}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {toast.type === 'success' ? '✓' : toast.type === 'error' ? '⚠' : 'ℹ'}
            </span>
            <span className="font-medium">{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-4 text-xl opacity-70 hover:opacity-100 transition-opacity"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">SLA Generator & Calculator</h1>
          <p className="text-foreground-secondary text-lg">
            Generate professional Service Level Agreements with uptime calculations, downtime budgets, and financial penalties.
          </p>
        </div>

        {/* Premium Feature Banner */}
        {!hasProAccess && (
          <div className="mb-6 bg-gradient-to-r from-accent-muted to-accent/5 border-2 border-accent/40 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-accent" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-foreground font-semibold">Excel Export is a Pro Feature</p>
                  <p className="text-foreground-secondary text-sm">Upgrade to Pro or Team to export comprehensive SLA documents with tier comparisons and legal templates.</p>
                </div>
              </div>
              <Link
                href="/pricing"
                className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors whitespace-nowrap"
              >
                Upgrade Now
              </Link>
            </div>
          </div>
        )}

        {/* Service Type Selection */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">1. Select Service Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {SERVICE_TYPES.map((type) => (
              <div
                key={type.id}
                onClick={() => handleServiceTypeChange(type)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  serviceType.id === type.id
                    ? 'border-accent bg-accent-muted'
                    : 'border-border hover:border-border/40'
                }`}
              >
                <h3 className="text-lg font-semibold text-foreground mb-2">{type.name}</h3>
                <p className="text-foreground-secondary text-sm">{type.description}</p>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-foreground text-sm font-medium mb-2">Custom Service Name (Optional)</label>
            <input
              type="text"
              placeholder={`e.g., ${serviceType.name} Premium`}
              value={customServiceName}
              onChange={(e) => setCustomServiceName(e.target.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded text-foreground placeholder-foreground-muted"
            />
          </div>
        </div>

        {/* SLA Tiers */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">2. SLA Tier Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-background">
                <tr>
                  <th className="p-3 text-foreground font-semibold">Tier</th>
                  <th className="p-3 text-foreground font-semibold">Uptime %</th>
                  <th className="p-3 text-foreground font-semibold">Monthly Downtime</th>
                  <th className="p-3 text-foreground font-semibold">Yearly Downtime</th>
                  <th className="p-3 text-foreground font-semibold">Response Time</th>
                  <th className="p-3 text-foreground font-semibold">Support</th>
                  <th className="p-3 text-foreground font-semibold">Select</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier, idx) => {
                  const downtime = calculateDowntime(tier.uptime);
                  return (
                    <tr key={idx} className={`border-t border-border-muted ${selectedTier.name === tier.name ? 'bg-accent-muted' : ''}`}>
                      <td className="p-3 text-foreground font-semibold">{tier.name}</td>
                      <td className="p-3 text-foreground">{tier.uptime}%</td>
                      <td className="p-3 text-foreground-secondary text-sm">{downtime.monthlyHours} hrs</td>
                      <td className="p-3 text-foreground-secondary text-sm">{downtime.yearlyHours} hrs ({downtime.yearlyDays} days)</td>
                      <td className="p-3 text-foreground-secondary text-sm">{tier.responseTime}</td>
                      <td className="p-3 text-foreground-secondary text-sm">{tier.supportHours}</td>
                      <td className="p-3">
                        <button
                          onClick={() => setSelectedTier(tier)}
                          className={`px-4 py-1 rounded text-sm font-semibold transition-colors ${
                            selectedTier.name === tier.name
                              ? 'bg-accent text-accent-foreground'
                              : 'bg-cream/20 text-foreground hover:bg-cream/30'
                          }`}
                        >
                          {selectedTier.name === tier.name ? 'Selected' : 'Select'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Financial Penalties */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">3. Financial Terms (Optional)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-foreground text-sm font-medium mb-2">Annual Revenue ($)</label>
              <input
                type="number" inputMode="numeric"
                value={annualRevenue}
                onChange={(e) => setAnnualRevenue(Number(e.target.value))}
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
              />
              <p className="text-foreground-tertiary text-xs mt-1">Customer&apos;s annual contract value</p>
            </div>
            <div>
              <label className="block text-foreground text-sm font-medium mb-2">Penalty Percentage (%)</label>
              <input
                type="number" inputMode="numeric"
                value={penaltyPercentage}
                onChange={(e) => setPenaltyPercentage(Number(e.target.value))}
                min="0"
                max="100"
                className="w-full px-4 py-2 bg-background border border-border rounded text-foreground"
              />
              <p className="text-foreground-tertiary text-xs mt-1">Service credit if SLA is breached</p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-background rounded-lg border border-border">
            <h3 className="text-lg font-semibold text-accent mb-3">Penalty Calculation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-foreground-secondary text-sm">If SLA Breached:</p>
                <p className="text-2xl font-bold text-foreground">${calculatePenalty(selectedTier.uptime).monthly}</p>
                <p className="text-foreground-tertiary text-xs">per month</p>
              </div>
              <div>
                <p className="text-foreground-secondary text-sm">Annual Impact:</p>
                <p className="text-2xl font-bold text-foreground">${calculatePenalty(selectedTier.uptime).total}</p>
                <p className="text-foreground-tertiary text-xs">maximum penalty</p>
              </div>
            </div>
          </div>
        </div>

        {/* Export Actions */}
        <div className="bg-surfaceer rounded-lg p-6 mb-8 border border-border">
          <h2 className="text-2xl font-bold text-accent mb-4">4. Export SLA Agreement</h2>
          <div className="flex flex-wrap gap-4">
            {hasProAccess ? (
              <button
                onClick={exportToExcel}
                className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-semibold hover:bg-accent-hover transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export to Excel
              </button>
            ) : (
              <Link
                href="/pricing"
                className="px-6 py-3 bg-accent-muted border-2 border-accent text-accent rounded-lg font-semibold hover:bg-accent/30 transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
                Upgrade to Export
              </Link>
            )}
            {commandCenterEnabled && !saved && (
              <button
                onClick={handleSaveToCommandCenter}
                disabled={saving}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? 'Saving...' : 'Save to Command Center'}
              </button>
            )}
            {saved && (
              <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved to Command Center
              </div>
            )}
            {saveError && (
              <div className="px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm">
                {saveError}
              </div>
            )}
          </div>
          <p className="text-foreground-tertiary text-sm mt-3">
            Excel export includes: SLA overview, tier comparison, selected tier details, full legal document template, and financial calculations.
          </p>
        </div>

        {/* How to Use */}
        <div className="bg-surfaceer rounded-lg p-6 border border-border">
          <h3 className="text-xl font-bold text-accent mb-4">How to Use</h3>
          <ol className="list-decimal list-inside space-y-2 text-foreground-secondary">
            <li><strong>Select Service Type</strong>: Choose from API, Platform, Infrastructure, Database, or CDN</li>
            <li><strong>Review Tiers</strong>: Compare Bronze, Silver, and Gold SLA tiers with uptime and downtime calculations</li>
            <li><strong>Select Tier</strong>: Choose the appropriate tier for your service level commitment</li>
            <li><strong>Set Financial Terms</strong>: Enter annual revenue and penalty percentage for credit calculations</li>
            <li><strong>Export to Excel</strong>: Generate a comprehensive SLA document with all details and templates</li>
          </ol>

          <div className="mt-6 p-4 bg-accent-muted border border-border rounded">
            <p className="text-foreground font-semibold mb-2">💡 Pro Tip:</p>
            <p className="text-foreground-secondary text-sm">
              The Gold tier (99.99% uptime) allows only 52.56 minutes of downtime per year. Most services start with Silver tier (99.9% = 8.76 hours/year) and upgrade as needed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
